import type { ReactNode } from 'react'
import type { Id } from '@/infra/id'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './MessageBankDialog.module.css'
import { SplitView, SplitViewMain, SplitViewSide } from '@/components/SplitView'
import {
  SplitViewSidebarListItem,
  SplitViewSidebarListItemArrow,
  SplitViewSidebarListItemTitle,
} from '@/components/SplitView/SplitViewSidebarListItem'
import { Sandwich, SandwichContent } from '@/components/Sandwich'
import { ScrollView } from '@/components/ScrollView'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { byDesign } from '@/infra/design'
import imgNojobstodisplayUrl from '@/assets/misc/img_nojobstodisplay.svg'
import NoJobsToDisplayIllustration from '@/components/SpotIllustration/NoJobsToDisplayIllustration'

export interface MessageBankReplyItem {
  id: Id
  title: string
  content: string
}

const MESSAGE_BANK_MAX_HEIGHT = 480

export interface MessageBankDialogProps {
  showing: boolean
  onClose: () => unknown
  isLoading: boolean
  hasReplies: boolean
  onUseSelectedReply: () => unknown
  children: ReactNode
}

export function MessageBankDialog({
  showing,
  onClose,
  isLoading,
  hasReplies,
  onUseSelectedReply,
  children,
}: MessageBankDialogProps) {
  return (
    <ModalSheet showing={showing} onRequestClose={onClose} large>
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Message Bank</ModalSheetTitle>
      </ModalSheetHeader>
      <ModalSheetContent>{children}</ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button label="Close" onPress={onClose} variant="default" />
          {hasReplies && (
            <Button
              label="Select Reply"
              onPress={onUseSelectedReply}
              isPending={isLoading}
              variant="primary"
              title={'Insert this reply into the message composer'}
              isDisabled={isLoading}
            />
          )}
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface MessageBankRepliesProps {
  replies: MessageBankReplyItem[]
  selectedReply: MessageBankReplyItem | null
  onSelectReply: (id: Id) => unknown
}

export function MessageBankReplies({
  replies,
  selectedReply,
  onSelectReply,
}: MessageBankRepliesProps) {
  return (
    <SplitView>
      <div className={styles.splitViewSideConstraint}>
        <SplitViewSide>
          <MessageBankList>
            {replies.map((reply) => (
              <MessageBankListItem
                active={selectedReply?.id === reply.id}
                onSelect={() => onSelectReply(reply.id)}
                key={reply.id}
                reply={reply}
              />
            ))}
          </MessageBankList>
        </SplitViewSide>
      </div>
      <SplitViewMain>
        {selectedReply && (
          <MessageBankItemDetail key={selectedReply.id} reply={selectedReply} />
        )}
      </SplitViewMain>
    </SplitView>
  )
}

export interface MessageBankEmptyProps {
  corporateMode: boolean
}

export function MessageBankEmpty({ corporateMode }: MessageBankEmptyProps) {
  return (
    <div className={styles.emptyStateContainer}>
      <EmptyState>
        {!corporateMode && (
          <EmptyStateIllustration
            src={imgNojobstodisplayUrl}
            illustration={byDesign(undefined, NoJobsToDisplayIllustration)}
          />
        )}
        <EmptyStateTitle>No replies yet</EmptyStateTitle>
        <EmptyStateDescription>
          Your workspace administrator hasn't configured any replies
        </EmptyStateDescription>
      </EmptyState>
    </div>
  )
}

interface MessageBankListProps {
  children: ReactNode
}

function MessageBankList({ children }: MessageBankListProps) {
  return (
    <Sandwich stretch>
      <SandwichContent>
        <ScrollView maxHeight={MESSAGE_BANK_MAX_HEIGHT}>{children}</ScrollView>
      </SandwichContent>
    </Sandwich>
  )
}

interface MessageBankListItemProps {
  reply: MessageBankReplyItem
  onSelect: () => void
  active?: boolean
}

function MessageBankListItem({
  reply,
  onSelect,
  active,
}: MessageBankListItemProps) {
  return (
    <SplitViewSidebarListItem
      spacing="compact"
      onPress={onSelect}
      active={active}
      extras={<SplitViewSidebarListItemArrow />}
    >
      <SplitViewSidebarListItemTitle wrap>
        {reply.title}
      </SplitViewSidebarListItemTitle>
    </SplitViewSidebarListItem>
  )
}

interface MessageBankItemDetailProps {
  reply: MessageBankReplyItem
}
function MessageBankItemDetail({ reply }: MessageBankItemDetailProps) {
  return (
    <ScrollView maxHeight={MESSAGE_BANK_MAX_HEIGHT}>
      <div className={styles.replyDetail}>{reply.content}</div>
    </ScrollView>
  )
}
